// lightly adapted from: https://www.notion.so/angelstudios/API-s-For-Client-November-24-139dbf5b32db8035bd9edacd43060d2b?pvs=4#139dbf5b32db809ca9ded98c6fba687e
import React from 'react'
import { gql, useMutation } from '@apollo/client'
import { validate as validateUuid } from 'uuid'
import {
  RecommendationTrack,
  TrackRecommendationMutation,
  TrackRecommendationMutationVariables,
} from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'

export const TRACK_RECOMMENDATION = gql`
  mutation trackRecommendation(
    $impressions: TrackRecommendationInput!
    $presses: TrackRecommendationInput!
    $includeImpressions: Boolean!
    $includePresses: Boolean!
  ) {
    impressions: trackRecommendation(input: $impressions) @include(if: $includeImpressions)
    presses: trackRecommendation(input: $presses) @include(if: $includePresses)
  }
`

type ThrottledTracker = {
  tracked: Set<string>
  throttled: RecommendationTrack[]
}

type TrackInput = {
  type: 'IMPRESSION' | 'PRESS'
  items: {
    id: string
  }[]
}

export const useRecommendationTracking = () => {
  const track = useSafeTrack()

  const [trackRecommendationMutation] = useMutation<TrackRecommendationMutation, TrackRecommendationMutationVariables>(
    TRACK_RECOMMENDATION,
  )

  const throttledImpressions = React.useRef<ThrottledTracker>({ tracked: new Set<string>(), throttled: [] })
  const throttledPresses = React.useRef<ThrottledTracker>({ tracked: new Set<string>(), throttled: [] })

  const flushTrackRecommendations = React.useCallback(() => {
    if (!throttledImpressions.current.throttled.length && !throttledPresses.current.throttled.length) {
      return
    }

    const impressions = throttledImpressions.current.throttled
    throttledImpressions.current.throttled = []

    const presses = throttledPresses.current.throttled
    throttledPresses.current.throttled = []

    return trackRecommendationMutation({
      variables: {
        impressions: {
          type: 'IMPRESSION',
          items: impressions,
        },
        presses: {
          type: 'PRESS',
          items: presses,
        },
        includeImpressions: impressions.length > 0,
        includePresses: presses.length > 0,
      },
      context: {
        requireAuth: true,
      },
    })
  }, [trackRecommendationMutation])

  React.useEffect(() => {
    const flushInterval = setInterval(flushTrackRecommendations, 10000)

    return () => {
      clearInterval(flushInterval)
      flushTrackRecommendations()
    }
  }, [flushTrackRecommendations])

  const trackRecommendation = React.useCallback(
    async (trackInput: TrackInput) => {
      const throttledTracker = trackInput.type === 'PRESS' ? throttledPresses.current : throttledImpressions.current

      const items: RecommendationTrack[] = trackInput.items
        .filter((item) => {
          if (!item?.id || throttledTracker.tracked.has(item.id)) {
            return false
          }

          if (!validateUuid(item?.id)) {
            logger().error('Attempted to track a Recommendation with an improper uuid', { item })
            return false
          }

          throttledTracker.tracked.add(item.id)
          return true
        })
        .map((item) => {
          const id = item.id
          const timestamp = new Date().toISOString()
          if (trackInput.type === 'PRESS') {
            track('Recommendation Press', { id, timestamp })
          }
          return { id, timestamp }
        })

      throttledTracker.throttled.push(...items)
    },
    [track],
  )

  return { trackRecommendation, flushTrackRecommendations }
}
